import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import type { Organization } from '@/types/database';
import { errorLogger } from '@/lib/error-logger';
import { logger } from '@/lib/structured-logger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  organization: Organization | null;
  signOut: () => Promise<void>;
  refetchOrganization: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const initialFetchDone = useRef(false);

  const fetchOrganization = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('organization_id, organizations(*)')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        // Check for infinite recursion error (RLS policy issue)
        if (error.message?.includes('infinite recursion')) {
          logger.error('DATABASE RLS POLICY ERROR: Infinite recursion detected', {
            details: 'A user_roles RLS policy is querying user_roles within itself',
            userId
          });
          setOrganization(null);
          return;
        }
        
        await errorLogger.log(error, {
          severity: 'error',
          additionalContext: { action: 'fetch_organization', userId }
        });
        return;
      }

      if (data?.organizations) {
        setOrganization(data.organizations as Organization);
      }
    } catch (error) {
      logger.error('Failed to fetch organization', { error, userId });
      // Gracefully fail - allow app to continue without organization
      setOrganization(null);
      
      await errorLogger.log(error as Error, {
        severity: 'error',
        additionalContext: { action: 'fetch_organization_catch', userId }
      });
    }
  };

  const refetchOrganization = async () => {
    if (user?.id) {
      await fetchOrganization(user.id);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Only synchronous state updates here
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Track login count for PWA install prompt (on SIGNED_IN event)
        if (event === 'SIGNED_IN' && session?.user) {
          const loginCount = parseInt(localStorage.getItem('growth-social-login-count') || '0', 10);
          localStorage.setItem('growth-social-login-count', String(loginCount + 1));
        }
        
        // Defer Supabase calls with setTimeout
        if (session?.user) {
          setTimeout(() => {
            fetchOrganization(session.user.id);
          }, 0);
        } else {
          setOrganization(null);
        }
      }
    );

    // THEN check for existing session ONLY ONCE
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      supabase.auth.getSession().then(async ({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          await fetchOrganization(session.user.id);
        }
        
        setLoading(false);
      });
    }

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setOrganization(null);
    navigate('/auth');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading, 
      organization,
      signOut,
      refetchOrganization 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
